import React, { useState } from 'react';
import { Eye, Clock, Activity, TrendingUp, BarChart3, Users, Monitor, Smartphone, Tablet, Globe, MapPin, Chrome, ExternalLink, Trash2 } from 'lucide-react';

const DeviceIcon = ({ type }) => {
  if (type === 'Mobile') return <Smartphone className="w-4 h-4" />;
  if (type === 'Tablet') return <Tablet className="w-4 h-4" />;
  return <Monitor className="w-4 h-4" />;
};

const BarRow = ({ label, count, maxCount, color = 'emerald', icon }) => {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5" data-testid={`bar-row-${label}`}>
      {icon && <span className="text-slate-500 flex-shrink-0">{icon}</span>}
      <span className="text-sm text-slate-700 w-32 truncate flex-shrink-0" title={label}>{label}</span>
      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full bg-${color}-500 transition-all duration-500`}
          style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color === 'emerald' ? '#10b981' : color === 'blue' ? '#3b82f6' : color === 'amber' ? '#f59e0b' : color === 'purple' ? '#8b5cf6' : '#10b981' }}
        />
      </div>
      <span className="text-sm font-semibold text-slate-700 w-10 text-right flex-shrink-0">{count}</span>
    </div>
  );
};

const SectionCard = ({ title, icon, children, testId }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-5" data-testid={testId}>
    <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
      {icon}
      {title}
    </h3>
    {children}
  </div>
);

const AnalyticsPanel = ({ analyticsSummary, pageAnalytics, analyticsTimeRange, setAnalyticsTimeRange, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="flex-1 p-4 md:p-6 overflow-auto" data-testid="analytics-panel">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-800" data-testid="analytics-title">Website Analytics</h2>
            <p className="text-sm text-slate-500">Track visitor engagement, locations, and devices</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                if (window.confirm('Are you sure you want to reset ALL statistics to zero? This cannot be undone.')) {
                  try {
                    const API = process.env.REACT_APP_BACKEND_URL;
                    await fetch(`${API}/api/analytics/reset?password=THEnewwebsite2026`, { method: 'POST' });
                    onRefresh();
                  } catch (err) {
                    console.error('Reset failed:', err);
                  }
                }
              }}
              className="px-3 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50 transition-colors flex items-center gap-1.5"
              data-testid="reset-analytics-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Reset
            </button>
            <select
              value={analyticsTimeRange}
              onChange={(e) => {
                setAnalyticsTimeRange(parseInt(e.target.value));
                onRefresh();
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              data-testid="analytics-time-range"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-6 overflow-x-auto" data-testid="analytics-tabs">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'visitors', label: 'Visitors & Locations' },
            { key: 'technology', label: 'Devices & Browsers' },
            { key: 'pages', label: 'Pages' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`analytics-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && analyticsSummary && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpi-cards">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <div className="flex items-center gap-2 text-emerald-600 mb-2">
                  <Users className="w-5 h-5" />
                  <span className="text-xs font-medium uppercase tracking-wide">Visitors</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-slate-800" data-testid="kpi-visitors">{analyticsSummary.unique_visitors}</p>
                <p className="text-xs text-slate-500">Unique visitors</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <Eye className="w-5 h-5" />
                  <span className="text-xs font-medium uppercase tracking-wide">Page Views</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-slate-800" data-testid="kpi-pageviews">{analyticsSummary.total_pageviews}</p>
                <p className="text-xs text-slate-500">Total views</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <div className="flex items-center gap-2 text-amber-600 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="text-xs font-medium uppercase tracking-wide">Avg Time</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-slate-800" data-testid="kpi-avg-time">
                  {analyticsSummary.avg_time_on_site >= 60 
                    ? `${Math.floor(analyticsSummary.avg_time_on_site / 60)}m ${Math.round(analyticsSummary.avg_time_on_site % 60)}s`
                    : `${Math.round(analyticsSummary.avg_time_on_site)}s`
                  }
                </p>
                <p className="text-xs text-slate-500">Per session</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <div className="flex items-center gap-2 text-purple-600 mb-2">
                  <Activity className="w-5 h-5" />
                  <span className="text-xs font-medium uppercase tracking-wide">Pages/Visit</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-slate-800" data-testid="kpi-pages-per-visit">{analyticsSummary.avg_pages_per_session.toFixed(1)}</p>
                <p className="text-xs text-slate-500">Per session</p>
              </div>
            </div>

            {/* Visitors Over Time Chart */}
            {analyticsSummary.visitors_by_day && analyticsSummary.visitors_by_day.length > 0 && (
              <SectionCard title="Visitors Over Time" icon={<TrendingUp className="w-5 h-5 text-emerald-600" />} testId="visitors-over-time-chart">
                <div className="h-48 md:h-64 flex items-end gap-1">
                  {analyticsSummary.visitors_by_day.map((day) => {
                    const maxVisitors = Math.max(...analyticsSummary.visitors_by_day.map(d => d.visitors));
                    const height = maxVisitors > 0 ? (day.visitors / maxVisitors) * 100 : 0;
                    return (
                      <div 
                        key={day.date} 
                        className="flex-1 flex flex-col items-center justify-end"
                        title={`${day.date}: ${day.visitors} visitors`}
                      >
                        <span className="text-xs text-slate-600 font-medium mb-1">{day.visitors}</span>
                        <div 
                          className="w-full bg-emerald-500 rounded-t-sm hover:bg-emerald-600 transition-colors cursor-pointer min-h-[4px]"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                        {analyticsSummary.visitors_by_day.length <= 14 && (
                          <span className="text-[10px] text-slate-400 mt-1 transform -rotate-45 origin-top-left whitespace-nowrap">
                            {day.date.slice(5)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {/* Quick Referrers */}
            {analyticsSummary.top_referrers && analyticsSummary.top_referrers.length > 0 && (
              <SectionCard title="Top Referrers" icon={<ExternalLink className="w-5 h-5 text-blue-600" />} testId="top-referrers-section">
                <div className="space-y-1">
                  {analyticsSummary.top_referrers.map((r, i) => (
                    <BarRow 
                      key={i} 
                      label={r.url ? new URL(r.url).hostname : 'Direct'} 
                      count={r.count} 
                      maxCount={analyticsSummary.top_referrers[0]?.count || 1} 
                      color="blue" 
                    />
                  ))}
                </div>
              </SectionCard>
            )}
          </div>
        )}

        {/* VISITORS & LOCATIONS TAB */}
        {activeTab === 'visitors' && analyticsSummary && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Countries */}
              <SectionCard title="Visitors by Country" icon={<Globe className="w-5 h-5 text-emerald-600" />} testId="visitors-by-country">
                {analyticsSummary.top_countries && analyticsSummary.top_countries.length > 0 ? (
                  <div className="space-y-1">
                    {analyticsSummary.top_countries.map((c, i) => (
                      <BarRow key={i} label={c.name} count={c.count} maxCount={analyticsSummary.top_countries[0]?.count || 1} color="emerald" icon={<Globe className="w-4 h-4" />} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-4 text-center">Location data will appear with new visitors</p>
                )}
              </SectionCard>

              {/* Top Cities */}
              <SectionCard title="Visitors by City" icon={<MapPin className="w-5 h-5 text-amber-600" />} testId="visitors-by-city">
                {analyticsSummary.top_cities && analyticsSummary.top_cities.length > 0 ? (
                  <div className="space-y-1">
                    {analyticsSummary.top_cities.map((c, i) => (
                      <BarRow 
                        key={i} 
                        label={`${c.city}${c.region ? `, ${c.region}` : ''}`} 
                        count={c.count} 
                        maxCount={analyticsSummary.top_cities[0]?.count || 1} 
                        color="amber" 
                        icon={<MapPin className="w-4 h-4" />} 
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-4 text-center">City data will appear with new visitors</p>
                )}
              </SectionCard>
            </div>

            {/* Recent Visitors Table */}
            <SectionCard title="Recent Visitors" icon={<Users className="w-5 h-5 text-purple-600" />} testId="recent-visitors-section">
              <RecentVisitorsTable />
            </SectionCard>
          </div>
        )}

        {/* TECHNOLOGY TAB */}
        {activeTab === 'technology' && analyticsSummary && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Devices */}
              <SectionCard title="Devices" icon={<Monitor className="w-5 h-5 text-blue-600" />} testId="devices-section">
                {analyticsSummary.devices && analyticsSummary.devices.length > 0 ? (
                  <div className="space-y-2">
                    {analyticsSummary.devices.map((d, i) => (
                      <BarRow key={i} label={d.name} count={d.count} maxCount={analyticsSummary.devices[0]?.count || 1} color="blue" icon={<DeviceIcon type={d.name} />} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-4 text-center">Device data will appear with new visitors</p>
                )}
              </SectionCard>

              {/* Browsers */}
              <SectionCard title="Browsers" icon={<Chrome className="w-5 h-5 text-amber-600" />} testId="browsers-section">
                {analyticsSummary.browsers && analyticsSummary.browsers.length > 0 ? (
                  <div className="space-y-2">
                    {analyticsSummary.browsers.map((b, i) => (
                      <BarRow key={i} label={b.name} count={b.count} maxCount={analyticsSummary.browsers[0]?.count || 1} color="amber" />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-4 text-center">Browser data will appear with new visitors</p>
                )}
              </SectionCard>

              {/* Operating Systems */}
              <SectionCard title="Operating Systems" icon={<Monitor className="w-5 h-5 text-purple-600" />} testId="os-section">
                {analyticsSummary.operating_systems && analyticsSummary.operating_systems.length > 0 ? (
                  <div className="space-y-2">
                    {analyticsSummary.operating_systems.map((o, i) => (
                      <BarRow key={i} label={o.name} count={o.count} maxCount={analyticsSummary.operating_systems[0]?.count || 1} color="purple" />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-4 text-center">OS data will appear with new visitors</p>
                )}
              </SectionCard>
            </div>
          </div>
        )}

        {/* PAGES TAB */}
        {activeTab === 'pages' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200" data-testid="page-performance-table">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                Page Performance
              </h3>
            </div>
            
            {pageAnalytics.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No analytics data yet</p>
                <p className="text-sm">Data will appear as visitors browse your website</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="text-left p-3">Page</th>
                      <th className="text-center p-3">Views</th>
                      <th className="text-center p-3">Visitors</th>
                      <th className="text-center p-3">Avg Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pageAnalytics.map((page) => (
                      <tr key={page.page_path} className="hover:bg-slate-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-slate-800">{page.page_title}</p>
                            <p className="text-xs text-slate-400">{page.page_path}</p>
                          </div>
                        </td>
                        <td className="text-center p-3">
                          <span className="inline-flex items-center justify-center min-w-[40px] px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            {page.views}
                          </span>
                        </td>
                        <td className="text-center p-3">
                          <span className="inline-flex items-center justify-center min-w-[40px] px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                            {page.unique_visitors}
                          </span>
                        </td>
                        <td className="text-center p-3 text-slate-600">
                          {page.avg_time >= 60 
                            ? `${Math.floor(page.avg_time / 60)}m ${Math.round(page.avg_time % 60)}s`
                            : `${Math.round(page.avg_time)}s`
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// Recent Visitors Sub-component (fetches its own data)
const RecentVisitorsTable = () => {
  const [visitors, setVisitors] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    const fetchVisitors = async () => {
      try {
        const res = await fetch(`${API}/api/analytics/recent-visitors?limit=25`);
        const data = await res.json();
        setVisitors(data.visitors || []);
      } catch (err) {
        console.error('Failed to fetch recent visitors:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVisitors();
  }, [API]);

  if (loading) return <p className="text-sm text-slate-400 py-4 text-center">Loading visitors...</p>;
  if (visitors.length === 0) return <p className="text-sm text-slate-400 py-4 text-center">No visitor data yet</p>;

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="overflow-x-auto" data-testid="recent-visitors-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left p-2">Last Active</th>
            <th className="text-left p-2">Location</th>
            <th className="text-center p-2">Device</th>
            <th className="text-center p-2">Browser</th>
            <th className="text-center p-2">Pages</th>
            <th className="text-center p-2">Time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {visitors.map((v, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="p-2 text-slate-600 whitespace-nowrap">{formatDate(v.last_activity)}</td>
              <td className="p-2">
                {v.city || v.country ? (
                  <span className="text-slate-700">{[v.city, v.region, v.country].filter(Boolean).join(', ')}</span>
                ) : (
                  <span className="text-slate-400 italic">Unknown</span>
                )}
              </td>
              <td className="text-center p-2">
                {v.device_type ? (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    v.device_type === 'Mobile' ? 'bg-blue-100 text-blue-700' :
                    v.device_type === 'Tablet' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    <DeviceIcon type={v.device_type} />
                    {v.device_type}
                  </span>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </td>
              <td className="text-center p-2 text-slate-600">{v.browser || '-'}</td>
              <td className="text-center p-2">
                <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                  {v.pages_visited}
                </span>
              </td>
              <td className="text-center p-2 text-slate-600">{formatTime(v.total_time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


export default AnalyticsPanel;
